import { colors } from '@lmc/common/constants'
import type React from 'react'
import { type ReactElement, type ReactNode, useMemo, useState } from 'react'
import './ListItem.less'

export type ListStateTypes =
  | 'default'
  | 'new'
  | 'archived'
  | 'signed'
  | 'refused'
  | 'unsigned'
  | 'unsigned'
  | 'simple'

export type ListItemProps = {
  iconContent: string | ReactNode
  onClick?: (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => void
  rightChildren?: ReactElement
  bottomChildren?: ReactElement
  state?: ListStateTypes
  style?: React.CSSProperties
  subtitleStyle?: React.CSSProperties
  subTitleContent?: string | ReactNode
  titleContent: string | ReactNode
  titleChildren?: string | ReactNode
  iconBackgroundColor?: string
  hoverRequired?: boolean
  children?: ReactNode
  className?: string
  cursor?: 'pointer' | 'default'
  hasBorder?: boolean
  alignChildrenTop?: boolean
}

const ListItem = ({
  iconContent,
  onClick,
  rightChildren,
  state,
  style,
  subtitleStyle,
  subTitleContent,
  titleContent,
  iconBackgroundColor,
  hoverRequired = false,
  cursor,
  children,
  hasBorder,
  titleChildren,
  bottomChildren,
  alignChildrenTop
}: ListItemProps) => {
  const isArchived = state === 'archived'

  const isSimple = state === 'simple'

  const getCursor = () => {
    if (cursor) {
      return cursor
    }

    return !isSimple || hoverRequired ? 'pointer' : 'default'
  }

  const [iconRef, setIconRef] = useState<HTMLDivElement | null>(null)
  const [rightChildrenRef, setRightChildrenRef] =
    useState<HTMLDivElement | null>(null)
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null)

  const titleMaxWidth = useMemo(() => {
    const iconWidth = iconRef?.offsetWidth || 0
    const rightChildrenWidth = rightChildrenRef?.offsetWidth || 0
    const containerWidth = containerRef?.offsetWidth || 0

    const otherWidthsSum =
      MAIN_CONTAINER_PADDING_LEFT + LEFT_CONTAINER_MARGIR_RIGHT

    const extraSpacing = 10

    const titleMaxWidth =
      containerWidth -
      (iconWidth + rightChildrenWidth + otherWidthsSum + extraSpacing)

    return titleMaxWidth
  }, [iconRef, rightChildren, containerRef])

  return (
    <div
      ref={setContainerRef}
      id={!isSimple || hoverRequired ? 'list-item' : ''}
      className={'list-container'}
      style={{
        ...styles.container,
        ...(hasBorder && styles.wrapperBorder),
        ...style,
        cursor: getCursor()
      }}
      onClick={onClick}
    >
      <div
        ref={setIconRef}
        style={{
          ...styles.leftBox,
          ...(alignChildrenTop && styles.alignChildrenTop),
          backgroundColor:
            iconBackgroundColor || (isSimple ? '#EFEEFF' : colors.darkblue)
        }}
      >
        {iconContent}
      </div>
      <div style={styles.subContainer}>
        <div>
          {titleChildren}
          <div
            style={{
              ...styles.singleTitle,
              maxWidth: titleMaxWidth
            }}
            className="title"
          >
            {titleContent}
          </div>
          <div
            style={{
              ...styles.subTitle,
              color: isSimple ? colors.darkgrey : 'inherit',
              ...subtitleStyle
            }}
          >
            {subTitleContent}
          </div>
          {bottomChildren}
        </div>
        <div
          ref={setRightChildrenRef}
          style={{ ...(alignChildrenTop && styles.alignChildrenTop) }}
        >
          {rightChildren || children}
        </div>
      </div>
    </div>
  )
}

const MAIN_CONTAINER_PADDING_LEFT = 22
const LEFT_CONTAINER_MARGIR_RIGHT = 14

const styles = {
  container: {
    background: colors.white,
    borderRadius: 15,
    display: 'flex',
    minHeight: '57px',
    opacity: 1,
    padding: `8px ${MAIN_CONTAINER_PADDING_LEFT}px`,
    transition: 'box-shadow 0.2s ease',
    width: '100%',
    alignItems: 'center'
  },
  wrapperBorder: {
    border: '1px solid',
    borderColor: '#D9D9D9'
  },
  leftBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: colors.white,
    backgroundColor: colors.darkblue,
    marginRight: LEFT_CONTAINER_MARGIR_RIGHT,
    width: 38,
    height: 38,
    borderRadius: 9,
    fontWeight: 700,
    minWidth: 38
  },
  singleTitle: {
    fontWeight: 700,
    fontSize: 17,
    // removed the flex display in order to use the elipsis
    // display: 'flex',
    display: 'block',
    lineHeight: '22px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: '100%'
  } as React.CSSProperties,
  subTitle: {
    fontSize: 13
  },
  subContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%'
  },
  alignChildrenTop: {
    marginBottom: 'auto'
  }
}

export default ListItem
